<script lang="ts">
  import { onMount } from 'svelte';
  import { push } from 'svelte-spa-router';
  import { auth } from '../../stores/auth.store';
  import PasswordInput from '../../components/PasswordInput.svelte';
  
  let email = '';
  let password = '';
  let loading = false;
  let error = '';
  
  onMount(() => {
    if (localStorage.getItem('user') === 'undefined') {
      localStorage.removeItem('user');
    }
  });
  
  async function handleLogin() {
    if (!email || !password) {
      error = 'Please fill in all fields';
      return;
    }
    
    loading = true;
    error = '';
    
    try {
      const result = await auth.login(email, password);
      
      if (result.success) {
        // Root route redirects to the role-based dashboard
      } else {
        error = 'Invalid credentials';
      }
    } catch (err) {
      error = 'An error occurred during login';
    } finally {
      loading = false;
    }
  }
</script>

<main class="login-page">
  <div class="form-container login-card">
    <header class="login-header">
      <div class="login-header-brand">
        <div class="login-header-logo-wrap" aria-hidden="true">
          <img
            src="https://give.leave2give.com.au/logo.svg"
            alt=""
            class="login-header-logo"
          />
        </div>
        <div class="login-header-text">
          <h1>Sign in</h1>
          <p class="login-subtitle">Welcome back to Leave2Give</p>
        </div>
      </div>
    </header>

    <form class="login-form" on:submit|preventDefault={handleLogin}>
      {#if error}
        <div class="error" role="alert">{error}</div>
      {/if}

      <section class="form-section" aria-labelledby="login-credentials-heading">
        <h2 id="login-credentials-heading" class="form-section-title">Your account</h2>
        <div class="form-section-body">
          <div class="form-group">
            <label for="email">Email address <span class="required" aria-label="required">*</span></label>
            <input
              type="email"
              id="email"
              bind:value={email}
              required
              disabled={loading}
              placeholder="Enter your email"
              autocomplete="email"
              aria-invalid={!!error}
            />
          </div>

          <div class="form-group password-group">
            <label for="password">Password <span class="required" aria-label="required">*</span></label>
            <PasswordInput
              id="password"
              bind:value={password}
              required={true}
              disabled={loading}
              placeholder="Enter your password"
              autocomplete="current-password"
            />
          </div>

          <div class="forgot-password-container">
            <a
              href="/forgot-password"
              class="forgot-password"
              on:click|preventDefault={() => push('/forgot-password')}
            >
              Forgot password?
            </a>
          </div>
        </div>
      </section>

      <div class="actions-login">
        <button type="submit" disabled={loading} class="login-btn">
          {loading ? 'Signing in...' : 'Sign in'}
        </button>
      </div>

      <div class="register-link">
        New to Leave2Give?
        <a href="/signup" on:click|preventDefault={() => push('/signup')}>Create an account</a>
      </div>
    </form>
  </div>
</main>

<style>
  .login-page {
    --auth-surface: #fffdf9;
    --auth-surface-muted: #faf8f4;
    --auth-ink: #1f2937;
    --auth-ink-muted: #6b7280;
    --auth-border: rgba(0, 0, 0, 0.08);
    --auth-shadow: 0 4px 24px rgba(52, 58, 64, 0.08);

    width: 100%;
    min-height: 100vh;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 2rem 1rem;
    box-sizing: border-box;
    background-color: #f5f3ef;
    background-image: url('/background.png');
    background-repeat: no-repeat;
    background-position: center;
    background-size: cover;
  }

  @keyframes login-card-enter {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .form-container.login-card {
    max-width: 440px;
    width: 100%;
    margin: 0 auto;
    padding: 1.375rem 1.625rem 1.5rem;
    background: var(--auth-surface);
    border: 1px solid var(--auth-border);
    border-radius: 16px;
    box-shadow: var(--auth-shadow);
    animation: login-card-enter 0.42s ease-out both;
  }

  .login-header {
    margin-bottom: 1.25rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  }

  .login-header-brand {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .login-header-logo-wrap {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.5rem;
    border-radius: 14px;
    background: var(--auth-surface-muted);
    border: 1px solid var(--auth-border);
  }

  .login-header-logo {
    height: 60px;
    width: auto;
    display: block;
  }

  .login-header-text h1 {
    margin: 0 0 0.25rem;
    text-align: left;
    font-size: 1.625rem;
    font-weight: 700;
    line-height: 1.15;
    letter-spacing: -0.02em;
    color: var(--auth-ink);
  }

  .login-subtitle {
    margin: 0;
    font-size: 0.875rem;
    color: var(--auth-ink-muted);
    line-height: 1.4;
    font-weight: 500;
  }

  .form-section {
    margin-bottom: 1.25rem;
  }

  .form-section-title {
    margin: 0 0 0.75rem;
    font-size: 0.6875rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--color-secondary);
  }

  .form-section-body {
    padding: 1rem 1.125rem;
    background: var(--auth-surface-muted);
    border: 1px solid rgba(0, 0, 0, 0.05);
    border-radius: 12px;
  }

  .form-group {
    margin-bottom: 0.875rem;
  }

  .form-section-body .form-group:last-of-type {
    margin-bottom: 0;
  }

  label {
    display: block;
    margin-bottom: 0.5rem;
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
  }

  .required {
    color: var(--color-negative);
  }

  input[type="email"] {
    width: 100%;
    padding: 0.75rem 1rem;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 0.875rem;
    outline: none;
    font-family: inherit;
    box-sizing: border-box;
    background: white;
    transition: border-color 0.2s, box-shadow 0.2s;
  }

  input::placeholder {
    color: rgba(0, 0, 0, 0.25);
  }

  input:focus {
    border-color: var(--color-primary);
    box-shadow: 0 0 0 3px rgba(255, 180, 0, 0.15);
  }

  input:disabled {
    background-color: #f3f4f6 !important;
    color: black !important;
    cursor: not-allowed;
    opacity: 0.7;
  }

  .password-group {
    position: relative;
  }

  .forgot-password-container {
    display: flex;
    justify-content: flex-end;
    margin-top: 0.75rem;
    padding-top: 0.75rem;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
  }

  .forgot-password {
    color: var(--color-secondary);
    text-decoration: none;
    font-size: 0.8125rem;
    font-weight: 500;
    transition: color 0.2s ease;
  }

  .forgot-password:hover {
    color: var(--auth-ink);
    text-decoration: underline;
  }

  .forgot-password:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(42, 157, 143, 0.2);
    border-radius: 4px;
  }

  .error {
    background-color: var(--color-negative-bg);
    color: var(--color-negative);
    padding: 0.75rem 1rem;
    border-radius: 8px;
    margin-bottom: 1rem;
    font-size: 0.875rem;
  }

  .actions-login {
    width: 100%;
    margin-top: 0.25rem;
  }

  .login-btn {
    width: 100%;
    padding: 0.9rem;
    background-color: var(--color-primary);
    color: var(--color-text-dark);
    border: none;
    border-radius: 25rem;
    cursor: pointer;
    font-size: 1rem;
    font-weight: 600;
    font-family: inherit;
    transition: background-color 0.2s ease, transform 0.1s ease, box-shadow 0.2s ease;
    box-shadow: 0 2px 10px rgba(255, 180, 0, 0.3);
  }

  .login-btn:hover:not(:disabled) {
    background-color: var(--color-primary-hover);
    transform: translateY(-1px);
    box-shadow: 0 4px 16px rgba(255, 180, 0, 0.35);
  }

  .login-btn:active:not(:disabled) {
    transform: translateY(0);
  }

  .login-btn:disabled {
    background-color: var(--color-disabled-bg);
    color: var(--color-disabled-text);
    cursor: not-allowed;
    box-shadow: none;
    transform: none;
  }

  .login-btn:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(255, 180, 0, 0.25);
  }

  .register-link {
    text-align: center;
    margin-top: 1.25rem;
    color: var(--auth-ink-muted);
    font-size: 0.875rem;
    line-height: 1.5;
  }

  .register-link a {
    color: var(--color-primary);
    text-decoration: none;
    font-weight: 600;
    margin-left: 0.25rem;
  }

  .register-link a:hover {
    text-decoration: underline;
  }

  @media (max-width: 768px) {
    .login-page {
      align-items: flex-start;
      padding: 1.25rem 0.5rem 1.5rem;
    }

    .form-container.login-card {
      padding: 1rem 1.125rem 1.25rem;
    }

    .login-header-logo-wrap {
      padding: 0.375rem;
      border-radius: 12px;
    }

    .login-header-logo {
      height: 48px;
    }

    .login-header-text h1 {
      font-size: 1.3125rem;
    }

    .login-subtitle {
      font-size: 0.8125rem;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .form-container.login-card {
      animation: none;
    }

    .login-btn {
      transition: none;
    }

    .login-btn:hover:not(:disabled) {
      transform: none;
    }
  }
</style>
